import type { AggregatedEngagement } from '@/types/emotion';

type Props = {
  data: AggregatedEngagement[];
};

type ClusteredEvent = {
  startTime: string;
  endTime: string;
  lowestEngagement: number;
  duration: number;
};

// Helper function to cluster consecutive low engagement points
const clusterInsights = (data: AggregatedEngagement[]): ClusteredEvent[] => {
  // Filter points where engagement dropped below 40%
  const lowPoints = data
    .filter((point) => point.engaged < 0.4)
    .map((point) => ({
      ...point,
      timestamp: new Date(point.timestamp).getTime(),
    }))
    .sort((a, b) => a.timestamp - b.timestamp);

  if (lowPoints.length === 0) return [];

  const clusters: ClusteredEvent[] = [];
  const CLUSTER_WINDOW_MS = 10 * 1000; // 10 seconds

  let currentCluster: {
    start: number;
    end: number;
    lowest: number;
  } | null = null;

  for (const point of lowPoints) {
    if (!currentCluster) {
      // Start new cluster
      currentCluster = {
        start: point.timestamp,
        end: point.timestamp,
        lowest: point.engaged,
      };
    } else if (point.timestamp - currentCluster.end <= CLUSTER_WINDOW_MS) {
      // Extend existing cluster
      currentCluster.end = point.timestamp;
      currentCluster.lowest = Math.min(currentCluster.lowest, point.engaged);
    } else {
      // Save current cluster and start new one
      clusters.push({
        startTime: new Date(currentCluster.start).toISOString(),
        endTime: new Date(currentCluster.end).toISOString(),
        lowestEngagement: currentCluster.lowest,
        duration: Math.floor((currentCluster.end - currentCluster.start) / 1000),
      });
      currentCluster = {
        start: point.timestamp,
        end: point.timestamp,
        lowest: point.engaged,
      };
    }
  }

  // Don't forget the last cluster
  if (currentCluster) {
    clusters.push({
      startTime: new Date(currentCluster.start).toISOString(),
      endTime: new Date(currentCluster.end).toISOString(),
      lowestEngagement: currentCluster.lowest,
      duration: Math.floor((currentCluster.end - currentCluster.start) / 1000),
    });
  }

  // Sort by lowest engagement (worst first) and take top 5
  return clusters
    .sort((a, b) => a.lowestEngagement - b.lowestEngagement)
    .slice(0, 5);
};

const formatTimeRange = (start: string, end: string): string => {
  const startDate = new Date(start);
  const endDate = new Date(end);
  
  // If same minute, show start time only
  if (startDate.getMinutes() === endDate.getMinutes() && 
      startDate.getHours() === endDate.getHours()) {
    return startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  
  return `${startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

export const LowEngagementTimeline = ({ data }: Props) => {
  const clusteredEvents = clusterInsights(data);

  return (
    <div className="rounded-2xl border border-background-border bg-background-surface p-6">
      <p className="text-sm font-semibold text-text-primary mb-1">Low Engagement Moments</p>
      <p className="text-xs text-text-dimmed mb-4">Significant drops below 40% engagement</p>
      {clusteredEvents.length === 0 ? (
        <p className="mt-4 text-sm text-text-dimmed">Great job! No significant engagement drops detected.</p>
      ) : (
        <ol className="mt-4 space-y-3 text-sm">
          {clusteredEvents.map((event, index) => (
            <li 
              key={`${event.startTime}-${index}`} 
              className="flex items-center justify-between rounded border border-neon-red/40 bg-neon-red/10 px-4 py-3"
            >
              <div className="flex flex-col">
                <span className="text-neon-red font-medium">
                  Significant Drop at {formatTimeRange(event.startTime, event.endTime)}
                </span>
                <span className="text-xs text-text-dimmed mt-1">
                  Duration: {event.duration}s
                </span>
              </div>
              <span className="text-neon-red font-semibold">
                {Math.round(event.lowestEngagement * 100)}% engaged
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
};

